import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useAuth = () => {
        const history = useHistory();
        const [isAuth, setIsAuth] = useState(false);
        const [loading, setLoading] = useState(true);
        const [user, setUser] = useState({});
        const [isLoggingOut, setIsLoggingOut] = useState(false);

        api.interceptors.request.use(
                config => {
                        const token = localStorage.getItem("token");
                        if (token) {
                                config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
                        }
                        return config;
                },
                error => {
                        Promise.reject(error);
                }
        );

        api.interceptors.response.use(
                response => {
                        return response;
                },
                async error => {
                        const originalRequest = error.config;
                        if (error?.response?.status === 403 && !originalRequest._retry) {
                                originalRequest._retry = true;

                                const { data } = await api.post("/auth/refresh_token");
                                if (data) {
                                        localStorage.setItem("token", JSON.stringify(data.token));
                                        api.defaults.headers.Authorization = `Bearer ${data.token}`;
                                }
                                return api(originalRequest);
                        }
                        if (error?.response?.status === 401 && !originalRequest.url.includes("/refresh_token")) {
                                localStorage.removeItem("token");
                                api.defaults.headers.Authorization = undefined;
                                setIsAuth(false);
                        }
                        return Promise.reject(error);
                }
        );

        useEffect(() => {
                const token = localStorage.getItem("token");
                (async () => {
                        if (isLoggingOut) return;

                        if (token) {
                                try {
                                        const cleanToken = JSON.parse(token);
                                        api.defaults.headers.Authorization = `Bearer ${cleanToken}`;
                                        setIsAuth(true);

                                        const { data } = await api.post("/auth/refresh_token");
                                        localStorage.setItem("token", JSON.stringify(data.token));
                                        api.defaults.headers.Authorization = `Bearer ${data.token}`;
                                        setUser(data.user);
                                        setIsAuth(true);
                                } catch (err) {
                                        console.error("Erro na revalidação silenciosa da sessão:", err);
                                        if (err?.response?.status === 401 || err?.response?.status === 403) {
                                                localStorage.removeItem("token");
                                                api.defaults.headers.Authorization = undefined;
                                                setIsAuth(false);
                                                history.push("/login");
                                        }
                                }
                        }
                        setLoading(false);
                })();
        }, [history, isLoggingOut]);

        useEffect(() => {
                const socket = openSocket();

                socket.on("user", data => {
                        if (data.action === "update" && data.user.id === user.id) {
                                setUser(data.user);
                        }
                });

                return () => {
                        socket.disconnect();
                };
        }, [user]);

        const handleLogin = async userData => {
                setLoading(true);

                try {
                        const { data } = await api.post("/auth/login", userData);
                        localStorage.setItem("token", JSON.stringify(data.token));
                        api.defaults.headers.Authorization = `Bearer ${data.token}`;
                        setUser(data.user);
                        setIsAuth(true);
                        toast.success(i18n.t("auth.toasts.success"));
                        history.push("/tickets");
                        setLoading(false);
                } catch (err) {
                        toastError(err);
                        setLoading(false);
                }
        };

        const handleLogout = async () => {
                setIsLoggingOut(true);
                setLoading(true);

                try {
                        await api.delete("/auth/logout");
                        setIsAuth(false);
                        setUser({});
                        localStorage.removeItem("token");
                        api.defaults.headers.Authorization = undefined;
                        setLoading(false);
                        history.push("/login");
                } catch (err) {
                        toastError(err);
                        setLoading(false);
                        setIsLoggingOut(false);
                }
        };

        return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
